// render-my-sources.ts — the "catalog-my-sources" renderer: the publisher's own sources dashboard
// (my_sources.php).
//
// Same JSON pipeline as the rest of the bundle (PHP compiles a flat payload from fetch_sources(); the
// browser holds it in memory and filters client-side, no jQuery) — but the card is the rich SOURCE
// management card: image/video header, product-type badges, price/release footer, AND a dense
// interactive management cluster (view/edit, media manager, private/delete). TypeScript owns the card
// shell, source media, and action buttons; PHP ships a pure-data JSON payload.
//
// Filtering is keyword (toolbar search box) + a product-type CHIP filter, both client-side — mirroring
// library.php / my_library.php (render-library.ts). The chips are rendered from the payload's
// product_types into the page's [data-ft-mysources-pt-chips] panel container; the toolbar filter
// button (data-ft-filter-button-id) closes the panel + re-filters, and chips also filter live on click.

import type { CatalogListConfig, CatalogMySourcesResponse, LibraryProductType, MySourceCard, MySourceGroup } from './types';
import { initTooltips, resolveUrl, results, setStatus, showLoader, hideLoader } from './catalog-list';
import { buildEmptyState, emptyStateFromAttributes } from '../shared/empty-state';
import { buildSourceMediaNode, resolveSourceMedia } from '../source-media/core';

const SOURCE_MEDIA_STYLE = 'max-width: 100%; max-height: 22rem; width: 100%; height: auto; object-fit: contain; vertical-align: top;';

// Close the filter panel (slideToggleEl if available, else hide) — same contract as render-library.
function closePanel(panelId: string | null): void {
    if (!panelId) return;
    if (typeof window.slideToggleEl === 'function') {
        window.slideToggleEl(panelId);
        return;
    }
    const panel = document.getElementById(panelId);
    if (panel) panel.style.display = 'none';
}

function icon(id: string, w = 20, h = 20, title: string | boolean = false): string {
    return typeof iconMap === 'function' ? iconMap(id, w, h, title) : '';
}

function appendIconButton(
    parent: HTMLElement,
    href: string,
    className: string,
    iconId: string,
    ariaLabel: string,
    title: string | boolean = false,
): void {
    const a = document.createElement('a');
    a.href = href;
    a.className = className;
    a.setAttribute('aria-label', ariaLabel);
    a.innerHTML = icon(iconId, 22, 22, title);
    parent.appendChild(a);
}

function appendActionButton(
    parent: HTMLElement,
    id: string,
    className: string,
    iconId: string,
    ariaLabel: string,
    title: string | boolean,
    display: string,
    onClick: () => void | Promise<void>,
): HTMLButtonElement {
    const button = document.createElement('button');
    button.id = id;
    button.type = 'button';
    button.className = className;
    button.style.display = display;
    button.setAttribute('aria-label', ariaLabel);
    button.innerHTML = icon(iconId, 25, 25, title);
    button.addEventListener('click', () => { void onClick(); });
    parent.appendChild(button);
    return button;
}

function renderSourceManagementActions(parent: HTMLElement, card: MySourceCard): void {
    const sid = card.item_id;
    const publicId = `source_public_${sid}`;
    const privateId = `source_private_${sid}`;
    const deleteId = `source_delete_${sid}`;
    const confirmId = `source_confirm_${sid}`;
    const privacyUrl = resolveUrl('options/db_queries/update/update_privacy_inc.php');
    const deleteUrl = resolveUrl('options/db_queries/delete/soft_delete_inc.php');

    const publicButton = appendActionButton(
        parent,
        publicId,
        'btn btn-primary btn-sm',
        'public',
        'Make private',
        'Public',
        card.is_private ? 'none' : 'block',
        async () => {
            await queryData(privacyUrl, 'div_sidelog_warning', {
                item_type: 'source',
                source_id: sid,
                source_private: '1',
            });
            publicButton.style.display = 'none';
            privateButton.style.display = 'block';
        },
    );

    const privateButton = appendActionButton(
        parent,
        privateId,
        'btn btn-danger btn-sm',
        'private',
        'Make public',
        'Private',
        card.is_private ? 'block' : 'none',
        async () => {
            await queryData(privacyUrl, 'div_sidelog_warning', {
                item_type: 'source',
                source_id: sid,
                source_private: '0',
            });
            privateButton.style.display = 'none';
            publicButton.style.display = 'block';
        },
    );

    const deleteButton = appendActionButton(
        parent,
        deleteId,
        'btn btn-warning btn-sm',
        'delete',
        'Delete source',
        'Delete',
        'block',
        () => {
            deleteButton.style.display = 'none';
            confirmButton.style.display = 'block';
        },
    );

    const confirmButton = appendActionButton(
        parent,
        confirmId,
        'btn btn-danger btn-sm',
        'confirm',
        'Confirm delete',
        'Confirm',
        'none',
        async () => {
            if (typeof openSideLog === 'function') openSideLog();
            await queryData(deleteUrl, 'div_sidelog_warning', {
                item_type: 'source',
                source_id: sid,
                display_success_sidelog: '1',
            }, true, true, true, `div_refresh_source_card_${sid}`);
        },
    );
}

function renderMySourceCard(card: MySourceCard, defaultCoverBase: string): HTMLElement {
    const media = resolveSourceMedia(card.pictures, card.videos, defaultCoverBase);
    const col = document.createElement('div');
    col.className = 'col-12 col-sm-6 col-lg-3 d-flex align-items-start px-2 py-2 mb-2';
    col.id = `div_refresh_source_card_${card.item_id}`;

    const shell = document.createElement('div');
    shell.className = 'card text-left w-100 shadow-sm d-flex flex-column ft-source-catalog-card ft-source-catalog-card--dtr border border-secondary border-opacity-25'
        + (media.kind === 'video' ? ' video-card-hover' : '');
    shell.setAttribute('data-bs-theme', 'dark');
    col.appendChild(shell);

    const mediaLink = document.createElement('a');
    mediaLink.href = card.url;
    mediaLink.className = 'ft-source-catalog-card__media d-flex align-items-center justify-content-center bg-dark flex-shrink-0 w-100 position-relative text-decoration-none';
    mediaLink.setAttribute('style', 'overflow: hidden; min-height: 5rem; max-height: 22rem;');
    mediaLink.setAttribute('aria-label', `View ${card.name || 'source'}`);
    mediaLink.appendChild(buildSourceMediaNode({
        pictures: card.pictures,
        videos: card.videos,
        defaultCoverBase,
        mediaClass: 'w-100 d-block',
        mediaStyle: SOURCE_MEDIA_STYLE,
        alt: card.name || '',
    }));
    shell.appendChild(mediaLink);

    const body = document.createElement('div');
    body.className = 'card-body py-2 px-3';
    shell.appendChild(body);

    const titleRow = document.createElement('div');
    titleRow.className = 'd-flex align-items-start justify-content-between gap-2 flex-wrap';
    body.appendChild(titleRow);

    const title = document.createElement('h4');
    title.className = 'card-title fs-6 lh-sm mb-0 fw-semibold text-white flex-grow-1 min-w-0';
    title.textContent = card.name || 'Untitled';
    titleRow.appendChild(title);

    if (card.review_status || card.is_private) {
        const badge = document.createElement('span');
        badge.className = 'badge bg-warning text-dark flex-shrink-0';
        badge.textContent = card.review_status || 'Private';
        titleRow.appendChild(badge);
    }

    if (Array.isArray(card.product_type_badges) && card.product_type_badges.length) {
        const badges = document.createElement('div');
        badges.className = 'd-flex flex-wrap gap-1 mt-2';
        badges.setAttribute('aria-label', 'Product types');
        for (const pt of card.product_type_badges) {
            if (!pt.name) continue;
            const badge = document.createElement('span');
            badge.className = 'badge bg-secondary bg-opacity-75 fw-normal text-wrap';
            badge.textContent = pt.name;
            badges.appendChild(badge);
        }
        body.appendChild(badges);
    }

    const footer = document.createElement('div');
    footer.className = 'card-footer ft-source-catalog-card__footer d-flex align-items-center justify-content-between gap-2 px-3 py-2 border-top border-secondary border-opacity-25 bg-transparent mt-auto';
    shell.appendChild(footer);

    const price = document.createElement('span');
    price.className = 'ft-source-catalog-card__price fw-semibold lh-sm';
    const priceInner = document.createElement('span');
    priceInner.className = card.price_class || 'text-success';
    priceInner.textContent = card.price_label || 'Free';
    price.appendChild(priceInner);
    footer.appendChild(price);

    if (card.release) {
        const release = document.createElement('span');
        release.className = 'ft-source-catalog-card__release text-muted small lh-sm text-end text-wrap';
        release.textContent = card.release;
        footer.appendChild(release);
    }

    const primaryActions = document.createElement('div');
    primaryActions.className = 'ft-source-catalog-card__actions border-top border-secondary border-opacity-25 px-3 py-2 pb-3 position-relative z-2 d-flex flex-wrap align-items-center gap-2';
    appendIconButton(primaryActions, card.url, 'btn btn-sm btn-light d-inline-flex align-items-center justify-content-center px-2', 'source', 'View source');
    appendIconButton(primaryActions, card.edit_url, 'btn btn-sm btn-warning d-inline-flex align-items-center justify-content-center px-2', 'edit', 'Edit source');
    primaryActions.querySelectorAll<HTMLElement>('.btn').forEach((btn) => {
        btn.style.minWidth = '2.35rem';
        btn.style.minHeight = '2rem';
    });
    shell.appendChild(primaryActions);

    const mediaActions = document.createElement('div');
    mediaActions.className = 'border-top border-secondary border-opacity-25 px-3 py-2';
    const mediaGroup = document.createElement('div');
    mediaGroup.className = 'd-flex flex-wrap align-items-center gap-2';
    appendIconButton(mediaGroup, card.media_url, 'btn btn-sm btn-info', 'modify_picture_gallery', 'Media manager', 'Media Manager - header galleries + image/video upload');
    mediaActions.appendChild(mediaGroup);
    shell.appendChild(mediaActions);

    if (card.manage_catalog !== false) {
    const serverActions = document.createElement('div');
    serverActions.className = 'border-top border-secondary border-opacity-25 px-3 py-2';
    const serverGroup = document.createElement('div');
    serverGroup.className = 'd-flex align-items-center flex-wrap gap-2';
    renderSourceManagementActions(serverGroup, card);
    serverActions.appendChild(serverGroup);
    shell.appendChild(serverActions);

    }
    return col;
}

// Render the product-type chips into the panel container (mirrors render-library.renderChips). Returns
// a getter for the currently-pressed ids.
function renderChips(container: HTMLElement, productTypes: LibraryProductType[], onChange: () => void): () => string[] {
    container.innerHTML = '';
    if (!productTypes.length) {
        const span = document.createElement('span');
        span.className = 'text-muted small';
        span.textContent = 'No catalog product types.';
        container.appendChild(span);
        return () => [];
    }
    const buttons: HTMLButtonElement[] = [];
    for (const pt of productTypes) {
        if (!pt.id || !pt.name) continue;
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'btn btn-sm rounded ft-catalog-pt-chip btn-dark border d-inline-flex align-items-center';
        btn.setAttribute('data-product-type-id', pt.id);
        btn.setAttribute('aria-pressed', 'false');
        btn.textContent = pt.name;
        btn.addEventListener('click', () => {
            const pressed = btn.getAttribute('aria-pressed') === 'true';
            btn.setAttribute('aria-pressed', pressed ? 'false' : 'true');
            btn.classList.toggle('btn-light', !pressed);
            btn.classList.toggle('btn-dark', pressed);
            btn.classList.toggle('border', pressed);
            onChange();
        });
        buttons.push(btn);
        container.appendChild(btn);
    }
    return () => buttons
        .filter((b) => b.getAttribute('aria-pressed') === 'true')
        .map((b) => b.getAttribute('data-product-type-id') || '')
        .filter(Boolean);
}

interface BuiltCard { el: HTMLElement; text: string; productTypes: string[]; }
interface BuiltGroup { heading: HTMLElement | null; cards: BuiltCard[]; }

export function mountCatalogMySources(root: HTMLElement, config: CatalogListConfig): void {
    const container = results(root);
    if (!container) {
        console.error('[catalog-my-sources] mount missing [data-ft-catalog-results]', root);
        return;
    }

    const searchInput = config.searchInputId
        ? (document.getElementById(config.searchInputId) as HTMLInputElement | null)
        : null;
    const emptyText = root.getAttribute('data-ft-empty-text') || 'No sources found.';
    // Product-type chips render into the page's filter panel (same container convention as library).
    const chipsContainer = document.querySelector('[data-ft-mysources-pt-chips]') as HTMLElement | null;

    const inlineData = readInlinePayload(root);
    if (inlineData) {
        hideLoader(root);
        setStatus(root, '');
        build(root, container, config, inlineData, searchInput, emptyText, chipsContainer);
        return;
    }

    void (async () => {
        const loadingTimer = setTimeout(() => showLoader(root, config), 250);
        try {
            const res = await fetch(resolveUrl(config.endpoint), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: '{}',
            });
            const raw = await res.text();
            let data: CatalogMySourcesResponse;
            try {
                data = JSON.parse(raw) as CatalogMySourcesResponse;
            } catch {
                throw new Error(`Non-JSON response (HTTP ${res.status}): ${raw.slice(0, 300)}`);
            }
            const errData = data as unknown as { error?: string; detail?: string };
            if (!res.ok || errData.error) {
                throw new Error(errData.detail || errData.error || `HTTP ${res.status}`);
            }
            clearTimeout(loadingTimer);
            hideLoader(root);
            setStatus(root, '');
            build(root, container, config, data, searchInput, emptyText, chipsContainer);
        } catch (e) {
            clearTimeout(loadingTimer);
            hideLoader(root);
            console.error('[catalog-my-sources] fetch/render failed', e);
            setStatus(root, '');
            container.innerHTML = '';
            const msg = document.createElement('div');
            msg.className = 'col-12 text-center text-muted py-4';
            msg.textContent = 'Could not load sources. Please try again.';
            container.appendChild(msg);
        }
    })();
}

function readInlinePayload(root: HTMLElement): CatalogMySourcesResponse | null {
    const payloadId = root.getAttribute('data-ft-payload-id');
    const el = payloadId
        ? document.getElementById(payloadId)
        : root.querySelector<HTMLScriptElement>('script[type="application/json"][data-ft-catalog-payload]');
    if (!el || !el.textContent) return null;
    try {
        const parsed = JSON.parse(el.textContent);
        return parsed && typeof parsed === 'object' ? parsed as CatalogMySourcesResponse : null;
    } catch (e) {
        console.error('[catalog-my-sources] inline payload parse failed', e);
        return null;
    }
}

// Section heading (Public / Private) — a full-width col so it spans the row grid, using the shared
// "sub-header system" (ft-catalog-section-title) the catalog pages use (my_campaigns / my_characters /
// my_adventures): first heading mb-3, subsequent get mt-5 for separation. An optional leading icon is
// resolved client-side via iconMap() (pure-data payload).
function sectionHeading(label: string, icon: string | null | undefined, first: boolean): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = first ? 'col-12 mb-3' : 'col-12 mt-5 mb-3';
    const h2 = document.createElement('h2');
    h2.className = 'ft-catalog-section-title';
    if (icon && typeof iconMap === 'function') {
        const ico = document.createElement('span');
        ico.innerHTML = iconMap(icon, 28, 28, false);
        h2.appendChild(ico);
        h2.appendChild(document.createTextNode(' '));
    }
    h2.appendChild(document.createTextNode(label));
    wrap.appendChild(h2);
    return wrap;
}

function build(
    root: HTMLElement,
    container: HTMLElement,
    config: CatalogListConfig,
    data: CatalogMySourcesResponse,
    searchInput: HTMLInputElement | null,
    emptyText: string,
    chipsContainer: HTMLElement | null,
): void {
    container.innerHTML = '';

    const groups: BuiltGroup[] = [];
    let firstHeading = true;
    for (const group of (data.groups || []) as MySourceGroup[]) {
        const cards = group.cards || [];
        if (!cards.length) continue;

        let heading: HTMLElement | null = null;
        if (group.label) {
            heading = sectionHeading(group.label, group.icon, firstHeading);
            firstHeading = false;
            container.appendChild(heading);
        }

        const built: BuiltCard[] = [];
        for (const card of cards) {
            const el = renderMySourceCard(card, data.default_cover_base || '');
            container.appendChild(el);
            built.push({
                el,
                text: card.text || (card.name || '').toLowerCase(),
                productTypes: Array.isArray(card.product_types) ? card.product_types : [],
            });
        }
        groups.push({ heading, cards: built });
    }

    const anyCards = groups.some((g) => g.cards.length > 0);

    // Init Bootstrap tooltips + hover-play videos on the freshly-built cards (same hydrate as the
    // my-adventures / library renderers; both skip already-initialized nodes).
    initTooltips(container);
    const initVideos = (window as unknown as { initializeVideoCards?: () => void }).initializeVideoCards;
    if (typeof initVideos === 'function') initVideos();

    // Empty-state at the top of the reserved min-height space.
    const emptyEl = document.createElement('div');
    emptyEl.className = 'col-12 text-center text-muted py-5';
    emptyEl.textContent = emptyText;
    emptyEl.style.display = anyCards ? 'none' : '';
    container.appendChild(emptyEl);
    const emptySpec = !anyCards ? emptyStateFromAttributes(root, emptyText) : null;
    if (emptySpec) {
        emptyEl.className = 'col-12';
        emptyEl.replaceChildren(buildEmptyState(emptySpec));
    }

    let getSelectedPtIds: () => string[] = () => [];

    const applyFilter = (): void => {
        const keyword = (searchInput?.value || '').trim().toLowerCase();
        const selected = getSelectedPtIds();
        const filtersActive = !!keyword || selected.length > 0;
        let totalVisible = 0;
        for (const group of groups) {
            let groupVisible = 0;
            for (const c of group.cards) {
                const matchKeyword = !keyword || c.text.includes(keyword);
                const matchType = selected.length === 0
                    || c.productTypes.some((id) => selected.includes(id));
                const show = matchKeyword && matchType;
                c.el.style.display = show ? '' : 'none';
                if (show) groupVisible++;
            }
            if (group.heading) group.heading.style.display = groupVisible > 0 ? '' : 'none';
            totalVisible += groupVisible;
        }
        if (!emptySpec) emptyEl.textContent = filtersActive ? 'No sources match your filters.' : emptyText;
        emptyEl.style.display = totalVisible === 0 ? '' : 'none';
    };

    if (chipsContainer) {
        getSelectedPtIds = renderChips(chipsContainer, data.product_types || [], applyFilter);
    }

    // Live keyword search + the house spyglass/clear trailing icons (same wiring as the other renderers).
    if (searchInput) {
        const box = searchInput.closest('.ft-inline-search-root');
        const glyph = box?.querySelector('[data-ft-inline-search-role="glyph"]') ?? null;
        const clear = box?.querySelector('[data-ft-inline-search-role="clear"]') ?? null;
        const syncTrailing = (): void => {
            if (!glyph || !clear) return;
            const hasText = (searchInput.value || '').trim().length > 0;
            glyph.classList.toggle('d-none', hasText);
            clear.classList.toggle('d-none', !hasText);
        };
        searchInput.addEventListener('input', () => { syncTrailing(); applyFilter(); });
        if (clear) {
            clear.addEventListener('click', (e) => {
                e.preventDefault();
                searchInput.value = '';
                searchInput.focus();
                syncTrailing();
                applyFilter();
            });
        }
        syncTrailing();
    }

    // Filter "apply" button: close the panel, then re-filter (chips already filter live on click).
    if (config.filterButtonId) {
        const btn = document.getElementById(config.filterButtonId);
        btn?.addEventListener('click', () => { closePanel(config.filterPanelId); applyFilter(); });
    }

    applyFilter();
}
